export type QuestionFlowOption = {
  id: string;
  label: string;
  description?: string;
};

export type QuestionFlowStep = {
  id: string;
  question: string;
  description?: string;
  options: QuestionFlowOption[];
  selectionMode?: "single" | "multiple";
  minSelections?: number;
  maxSelections?: number;
  inputMode?: "choice" | "text" | "files";
  optional?: boolean;
};

/** Sentinel stored when the user skips an optional text/files step. */
export const QUESTION_FLOW_TEXT_SKIP = "__skip__";

export type QuestionFlowSupportingDocument = {
  fileName: string;
  docType: string;
  fileBase64: string;
};

export type QuestionFlowCompleteExtras = {
  supportingDocuments?: QuestionFlowSupportingDocument[];
};

function isQuestionOption(value: unknown): value is QuestionFlowOption {
  if (!value || typeof value !== "object") return false;
  const option = value as Partial<QuestionFlowOption>;
  return typeof option.id === "string" && option.id.length > 0 && typeof option.label === "string";
}

/** Drops steps that are still streaming and are not yet ready to show. */
export function completeQuestionSteps(steps: unknown): QuestionFlowStep[] {
  if (!Array.isArray(steps)) return [];
  const ready: QuestionFlowStep[] = [];
  for (const step of steps) {
    if (!step || typeof step !== "object") continue;
    const candidate = step as Partial<QuestionFlowStep>;
    if (typeof candidate.id !== "string" || candidate.id.length === 0) continue;
    if (typeof candidate.question !== "string" || candidate.question.length === 0) continue;
    if (candidate.inputMode === "text" || candidate.inputMode === "files") {
      ready.push({
        ...candidate,
        id: candidate.id,
        question: candidate.question,
        options: Array.isArray(candidate.options)
          ? candidate.options.filter(isQuestionOption)
          : [],
        inputMode: candidate.inputMode,
        optional: candidate.optional === true,
      });
      continue;
    }
    if (!Array.isArray(candidate.options) || !candidate.options.every(isQuestionOption)) continue;
    if (candidate.options.length === 0) continue;
    ready.push({
      ...candidate,
      id: candidate.id,
      question: candidate.question,
      options: candidate.options,
      inputMode: "choice",
    });
  }
  return ready;
}
